import { Calendar, Mail, MessageSquare, Phone, User } from "lucide-react";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { PriceDisplay } from "@/components/ui/price-display";
import {
  getCustomerJoinDate,
  getCustomerPrimaryPhone,
} from "@/domains/customer/customers/utils/customer-utils";
import type { CustomerDetailFragment } from "@/gql/graphql";
import { useConfig } from "@/hooks/use-config";
import { useMarketingConsent } from "../../hooks/use-marketing-consent";
import { CustomerMetricsDashboard } from "./customer-metrics-dashboard";

interface CustomerOverviewTabProps {
  customer: CustomerDetailFragment;
  onCall?: () => void;
  onEmail?: () => void;
  onBookAppointment?: () => void;
}

export function CustomerOverviewTab({
  customer,
  onCall,
  onEmail,
  onBookAppointment,
}: CustomerOverviewTabProps) {
  const { t } = useTranslation("customers");
  const { value: loyaltyEnabled = false } = useConfig("loyalty.enabled");
  const tierName = customer.currentTier?.name || t("list.item.noTier");
  const joinDate = getCustomerJoinDate(customer);
  const avgSpendPerVisit =
    customer.totalVisits > 0 ? customer.totalSpent / customer.totalVisits : 0;
  const primaryPhone = getCustomerPrimaryPhone(customer);

  // Get marketing consent data
  const { marketingConsent, loading: consentLoading } = useMarketingConsent({
    customerId: customer.id,
  });

  return (
    <div className="space-y-6">
      {/* Key Metrics Dashboard */}
      <CustomerMetricsDashboard
        customer={customer}
        onCall={onCall}
        onEmail={onEmail}
        onBookAppointment={onBookAppointment}
      />

      {/* Personal Information and Insights */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Personal Information */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="w-4 h-4" />
              {t("detail.personalInfo.title")}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Contact Details */}
            <div className="space-y-3">
              {primaryPhone && (
                <div className="flex items-center gap-3">
                  <Phone className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                  <a
                    href={`tel:${primaryPhone}`}
                    className="text-sm hover:text-primary hover:underline cursor-pointer"
                  >
                    {primaryPhone}
                  </a>
                </div>
              )}
              {customer.email && (
                <div className="flex items-center gap-3">
                  <Mail className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                  <a
                    href={`mailto:${customer.email}`}
                    className="text-sm truncate hover:text-primary hover:underline cursor-pointer"
                  >
                    {customer.email}
                  </a>
                </div>
              )}
              {customer.dateOfBirth && (
                <div className="flex items-center gap-3">
                  <Calendar className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                  <span className="text-sm">
                    {DateTime.fromISO(customer.dateOfBirth).toLocaleString(DateTime.DATE_MED)}
                  </span>
                </div>
              )}
            </div>

            {/* Membership Info */}
            <div className="pt-4 border-t space-y-3">
              <div>
                <div className="text-xs text-muted-foreground mb-1">
                  {t("detail.personalInfo.memberSince")}
                </div>
                <div className="font-medium">{joinDate.toLocaleString(DateTime.DATE_MED)}</div>
              </div>

              {loyaltyEnabled && (
                <div>
                  <div className="text-xs text-muted-foreground mb-1">
                    {t("detail.personalInfo.currentTier")}
                  </div>
                  <Badge
                    className="capitalize"
                    style={{
                      backgroundColor: customer.currentTier?.color || "#64748b",
                      color: "white",
                    }}
                  >
                    {tierName} {t("detail.personalInfo.member")}
                  </Badge>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Customer Insights */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MessageSquare className="w-4 h-4" />
              {t("detail.insights.title")}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Quick Stats */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-muted/50 p-3 rounded-lg text-center">
                <div className="text-lg font-semibold text-foreground">
                  <PriceDisplay amount={avgSpendPerVisit} />
                </div>
                <div className="text-xs text-muted-foreground">
                  {t("detail.insights.avgPerVisit")}
                </div>
              </div>
              <div className="bg-muted/50 p-3 rounded-lg text-center">
                <div className="text-lg font-semibold text-foreground">
                  {customer.totalVisits > 0
                    ? Math.floor((Date.now() - joinDate.toMillis()) / (1000 * 60 * 60 * 24 * 30))
                    : 0}
                </div>
                <div className="text-xs text-muted-foreground">
                  {t("detail.insights.monthsActive")}
                </div>
              </div>
            </div>

            {/* Communication Consent */}
            <div className="space-y-3 pt-2 border-t">
              <div>
                <div className="text-xs text-muted-foreground mb-2">
                  {t("detail.insights.communicationConsent")}
                </div>
                <div className="space-y-1 text-xs">
                  <div className="flex justify-between">
                    <span>{t("detail.insights.emailMarketing")}</span>
                    {consentLoading ? (
                      <Badge variant="outline" className="text-xs">
                        Loading...
                      </Badge>
                    ) : (
                      <Badge
                        variant={marketingConsent.email ? "default" : "secondary"}
                        className="text-xs"
                      >
                        {marketingConsent.email
                          ? t("notifications.optedIn")
                          : t("notifications.optedOut")}
                      </Badge>
                    )}
                  </div>
                  <div className="flex justify-between">
                    <span>{t("detail.insights.smsMarketing")}</span>
                    {consentLoading ? (
                      <Badge variant="outline" className="text-xs">
                        Loading...
                      </Badge>
                    ) : (
                      <Badge
                        variant={marketingConsent.sms ? "default" : "secondary"}
                        className="text-xs"
                      >
                        {marketingConsent.sms
                          ? t("notifications.optedIn")
                          : t("notifications.optedOut")}
                      </Badge>
                    )}
                  </div>
                </div>
                <div className="text-xs text-muted-foreground mt-2">
                  {/* TODO: Load from ConsentService and show consent date/source */}
                  {t("detail.insights.consentDescription")}
                  {/* Last updated: {consentDate.toLocaleString(DateTime.DATE_SHORT)} */}
                </div>
              </div>
            </div>

            {/* Notes */}
            {customer.notes && (
              <div className="pt-2 border-t">
                <div className="text-xs text-muted-foreground mb-2">
                  {t("detail.insights.notes")}
                </div>
                <div className="text-sm bg-muted/50 p-3 rounded-lg">{customer.notes}</div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
